import { Suspense, lazy } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { resolvePmsLinkSlug } from './resolvePmsLinkSlug';

/**
 * Loaded through the MF runtime, never at build time (root CLAUDE.md rule 1).
 * `lazy` also keeps the PMS bundle out of the shell's entry chunk — this route
 * is opened by employees arriving from an email, not by anyone browsing the app.
 */
const PmsTokenForm = lazy(() => import('clarixPms/TokenForm'));

/**
 * The PUBLIC evaluation link — `/pms/eval/:token` (PMS contract §12, D-15).
 *
 * Mounted OUTSIDE `RequireAuth`, alongside `/invites/:token`. The employee
 * arrives from an invitation email and the token in the URL is the credential;
 * requiring a login here would lock out every directory-only member, who has no
 * Clarix account at all (`WorkspaceMembership.user_id = null`) — see
 * divergences.md §D-15, which struck FR-301's login clause.
 *
 * The slug is resolved HERE and handed to the remote as a prop: `getWorkspaceSlug`
 * is shell-owned and a remote may not import shell source.
 */
export function EvaluationLinkPage() {
  const { token } = useParams<{ token: string }>();
  const [search] = useSearchParams();
  const slug = resolvePmsLinkSlug(search.get('ws'));

  if (!slug || !token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <div className="w-full max-w-md rounded-xl border border-border bg-background p-8 text-center shadow-sm">
          <h1 className="mb-2 text-lg font-semibold text-foreground">This link is not valid</h1>
          <p className="text-sm text-muted-foreground">
            {token
              ? 'This link does not say which workspace it belongs to. Open it from your invitation email, which carries the full address.'
              : 'Open the most recent invitation email and use the link exactly as it appears there.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-muted/30">
          <span className="text-sm text-muted-foreground">Opening your evaluation…</span>
        </div>
      }
    >
      <PmsTokenForm slug={slug} token={token} />
    </Suspense>
  );
}
